/**
 * Requests over time, as bars.
 *
 * Plain elements rather than a charting library: one chart of one shape does not justify a
 * dependency, and the dashboard keeps its interface primitives local (see `ui.tsx`). Each bar is the
 * requests in its slot, with the cache hits among them shaded lighter. An empty slot is a gap on the
 * baseline, so a quiet stretch reads as quiet rather than disappearing.
 */
import {
  bucketSizeFor,
  bucketize,
  rangeDays,
  type BucketSize,
  type HourlyUsage,
  type UsageBucket,
} from '../lib/usage-buckets';

const SLOT_NAMES: Record<BucketSize, string> = {
  hour: 'hour',
  'six-hours': '6 hours',
  day: 'day',
};

function count(value: number, singular: string, plural: string): string {
  return `${value.toLocaleString('en-US')} ${value === 1 ? singular : plural}`;
}

function formatDay(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

/** What a slot covers: "Sep 14, 12:00–18:00", or "Sep 14" for a whole day. */
function slotLabel(bucket: UsageBucket, size: BucketSize): string {
  return size === 'day'
    ? formatDay(bucket.start)
    : `${formatDay(bucket.start)}, ${formatTime(bucket.start)}–${formatTime(bucket.end)}`;
}

/** The label under a bar: the hour on a day's chart, the date otherwise. */
function tickLabel(bucket: UsageBucket, size: BucketSize): string {
  return size === 'hour' ? formatTime(bucket.start) : formatDay(bucket.start);
}

/** One decimal: finer than a pixel at this height, without floating-point noise in the markup. */
function share(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.round(Math.min(part / whole, 1) * 1000) / 10;
}

export function RequestsChart({
  hourly,
  from,
  to,
}: {
  hourly: readonly HourlyUsage[];
  /** The range the usage was read for, as the API reports it. */
  from: string;
  to: string;
}) {
  const start = new Date(from);
  const end = new Date(to);
  const days = rangeDays(start, end);
  const size = bucketSizeFor(days);
  const buckets = bucketize(hourly, start, end, size);

  const first = buckets[0];
  const middle = buckets[Math.floor(buckets.length / 2)];
  const last = buckets.at(-1);
  if (first === undefined || middle === undefined || last === undefined) return null;

  const peak = Math.max(...buckets.map((bucket) => bucket.requests));
  const total = buckets.reduce((sum, bucket) => sum + bucket.requests, 0);
  const slot = SLOT_NAMES[size];
  const range = days === 1 ? '24 hours' : `${String(days)} days`;

  return (
    <figure className="mt-4" data-testid="requests-chart">
      <div className="flex gap-3">
        <div
          className="flex h-40 flex-col justify-between text-right text-xs text-neutral-500"
          aria-hidden="true"
        >
          {/* No top label for an empty range: a scale from 0 to 0 says nothing. */}
          <span>{peak > 0 ? peak.toLocaleString('en-US') : ''}</span>
          <span>0</span>
        </div>

        <div className="min-w-0 flex-1">
          <div
            role="img"
            aria-label={`Requests over the last ${range}, per ${slot}: ${count(total, 'request', 'requests')} in total, peak ${peak.toLocaleString('en-US')} per ${slot}.`}
            className="relative flex h-40 items-end gap-px border-b border-neutral-300"
          >
            {buckets.map((bucket) => (
              <div
                key={bucket.start.getTime()}
                className="flex h-full min-w-0 flex-1 items-end rounded-t-sm hover:bg-neutral-100"
                title={`${slotLabel(bucket, size)} · ${count(bucket.requests, 'request', 'requests')} · ${count(bucket.cacheHits, 'cache hit', 'cache hits')}`}
                data-testid="requests-chart-bar"
              >
                <div
                  className="flex w-full flex-col justify-end overflow-hidden rounded-t-sm bg-brand-400"
                  style={{
                    height: `${String(share(bucket.requests, peak))}%`,
                    // A slot with one request among thousands still shows as more than nothing.
                    minHeight: bucket.requests > 0 ? 2 : 0,
                  }}
                >
                  <div
                    className="w-full bg-brand-600"
                    style={{ height: `${String(share(bucket.cacheHits, bucket.requests))}%` }}
                  />
                </div>
              </div>
            ))}
            {/*
              The empty range is still drawn -- its slots, its baseline, its dates -- so the page keeps
              its shape when nothing happened. This says so, rather than leaving a blank that could
              be mistaken for a chart that failed to load.
            */}
            {total === 0 && (
              <p
                className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-neutral-500"
                data-testid="requests-chart-empty"
              >
                No requests in this range
              </p>
            )}
          </div>

          <div className="mt-1 flex justify-between text-xs text-neutral-500" aria-hidden="true">
            <span>{tickLabel(first, size)}</span>
            <span>{tickLabel(middle, size)}</span>
            <span>{tickLabel(last, size)}</span>
          </div>
        </div>
      </div>

      <figcaption className="mt-3 flex gap-4 text-xs text-neutral-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-brand-400" aria-hidden="true" />
          Requests
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-brand-600" aria-hidden="true" />
          Cache hits
        </span>
      </figcaption>
    </figure>
  );
}
